import React from "react";
import { ReactComponent as AttachIcon } from "../../assets/img/common/attach-file-icon.svg";
import useNavigateToProfile from "../../hooks/useNavigateToProfile";
import { UN_REGISTERED } from "../../Constants/index";
import UserPlaceHolderImg from "../../assets/img/common/user-placeholder-img.png";
import ExternalLink from "../../components/common/ExternalLink";
import { contractNetworks } from "../../utils/currencies";
import useWindowSize from "../../hooks/useWindowSize";
import LexicalEditor from "../../components/common/LexicalEditor";
import { convertHtmlToText } from "../../utils/general";
import { useGetUserQuery } from "../../redux/slices/api/userApi";
import { Bars } from "react-loader-spinner";
import CopyContent from "../../components/common/CopyContent";
import WalletDisplay from "../../components/common/WalletDisplay";
import { HiOutlineInformationCircle, HiOutlineUserGroup, HiOutlineLink, HiOutlinePaperClip, HiOutlineExternalLink } from "react-icons/hi";

const isHtmlFormat = (text) => /<\/?[a-z][\s\S]*>/i.test(text);

const DetailsTab = ({ data }) => {
  const {
    summary,
    beneficiaries,
    attachment,
    nft_details,
    collaborators,
    equipment,
    links,
  } = data ?? {};

  const { isLoading: getMeLoading, data: userData } = useGetUserQuery({
    skip: !!data?.creator,
  });

  const creator = data?.creator ?? userData?.user ?? {};
  const { navigateToProfile } = useNavigateToProfile();
  const { windowSize } = useWindowSize();

  return (
    <div className="w-full flex flex-col pt-6 roboto-font">
      {/* Top Section - Information & Team */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-10">
        {/* Information Card */}
        <div className="flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary to-[#7e7cff] flex items-center justify-center">
              <HiOutlineInformationCircle className="w-5 h-5 text-white" />
            </div>
            <h5 className="text-xl font-bold text-secondary">Information</h5>
          </div>
          <div className="flex-1 rounded-2xl bg-gray-50/80 overflow-hidden shadow-sm">
            <div className="flex justify-between py-4 px-5 bg-white/50">
              <span className="text-gray-600">Token Standard</span>
              <span className="font-medium text-secondary">{nft_details?.token_standard || "ERC-721"}</span>
            </div>
          </div>
        </div>
        
        {/* Team Card */}
        <div className="flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary to-[#7e7cff] flex items-center justify-center">
              <HiOutlineUserGroup className="w-5 h-5 text-white" />
            </div>
            <h5 className="text-xl font-bold text-secondary">Meet the Team</h5>
          </div>
          <div className="flex-1 rounded-2xl bg-gray-50 border border-gray-200 overflow-hidden max-h-64 overflow-y-auto custom-primary-tiny-scroll">
            {getMeLoading ? (
              <div className="flex items-center justify-center py-8">
                <Bars height="30" width="30" color="#8538ff" visible={true} />
              </div>
            ) : creator ? (
              <div
                onClick={() =>
                  creator?.role !== UN_REGISTERED &&
                  navigateToProfile(creator?._id, creator?.role)
                }
                className="flex items-center justify-between py-4 px-5 border-b border-gray-200 cursor-pointer hover:bg-white transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={creator?.images?.profile ?? UserPlaceHolderImg}
                    alt="Creator"
                    className="w-12 h-12 rounded-full border-2 border-white shadow-md group-hover:scale-105 transition-transform"
                  />
                  <div className="flex flex-col">
                    <span className="font-semibold text-secondary group-hover:text-primary transition-colors">
                      {creator?.fundername || `${creator?.firstname} ${creator?.lastname}`}
                    </span>
                    <span className="text-sm text-gray-500 truncate max-w-[180px]">
                      {creator?.title_rank || creator?.location || creator?.email}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <CopyContent
                    value={creator.public_address}
                    className="text-primary text-xs font-mono hover:underline"
                  >
                    <WalletDisplay address={creator.public_address} />
                  </CopyContent>
                  <span className="text-xs font-bold text-white bg-gradient-to-r from-primary to-[#7e7cff] px-2 py-0.5 rounded-full mt-1">
                    CREATOR
                  </span>
                </div>
              </div>
            ) : null}
            {collaborators?.length
              ? collaborators?.map((item, index, arr) => (
                  <div
                    key={index}
                    onClick={() =>
                      item?.role !== UN_REGISTERED &&
                      navigateToProfile(item?._id, item?.role)
                    }
                    className={`flex items-center justify-between py-4 px-5 cursor-pointer hover:bg-white transition-colors group`}
                  >
                    <div className="flex items-center gap-3">
                      <img
                        src={item?.images?.profile ?? UserPlaceHolderImg}
                        alt="Collaborator"
                        className="w-12 h-12 rounded-full border-2 border-white shadow-md group-hover:scale-105 transition-transform"
                      />
                      <div className="flex flex-col">
                        <span className="font-semibold text-secondary group-hover:text-primary transition-colors">
                          {item?.fundername || `${item?.firstname} ${item?.lastname}`}
                        </span>
                        <span className="text-sm text-gray-500 truncate max-w-[180px]">
                          {item?.title_rank || item?.location || item?.email}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col items-end">
                      <CopyContent
                        value={item?.public_address}
                        className="text-primary text-xs font-mono hover:underline"
                        id={index + 2}
                      >
                        <WalletDisplay address={item?.public_address} />
                      </CopyContent>
                      <span className="text-xs font-medium text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full mt-1">
                        COLLABORATOR
                      </span>
                    </div>
                  </div>
                ))
              : null}
          </div>
        </div>
      </div>
      
      {/* Content Sections */}
      <div className="flex flex-col space-y-10">
        {/* Project Summary */}
        {summary?.trim()?.length > 0 && (
          <div className="p-6 rounded-2xl bg-gradient-to-br from-gray-50/80 to-white shadow-sm">
            <h5 className="text-xl font-bold text-secondary mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Project Summary
            </h5>
            {isHtmlFormat(summary) ? (
              <div 
                id="summary" 
                className="prose prose-gray max-w-none overflow-auto break-words" 
                dangerouslySetInnerHTML={{ __html: summary }} 
              />
            ) : (
              <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                {summary}
              </p>
            )}
          </div>
        )}
        
        {/* Beneficiaries */}
        {convertHtmlToText({ htmlContent: beneficiaries }) && (
          <div className="p-6 rounded-2xl bg-gradient-to-br from-gray-50/80 to-white shadow-sm">
            <h5 className="text-xl font-bold text-secondary mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              Beneficiaries
            </h5>
            <LexicalEditor
              isContentPreview={true}
              disabled={true}
              id={"beneficiaries"}
              value={beneficiaries}
              containerClass="w-full"
            />
          </div>
        )}
        
        {/* Equipment */}
        {convertHtmlToText({ htmlContent: equipment }) && (
          <div className="p-6 rounded-2xl bg-gradient-to-br from-gray-50/80 to-white shadow-sm">
            <h5 className="text-xl font-bold text-secondary mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              Tools, Equipment & Materials
            </h5>
            <LexicalEditor
              isContentPreview={true}
              disabled={true}
              id={"equipment"}
              value={equipment}
              containerClass="w-full"
            />
          </div>
        )}
      </div>
      
      {/* Attachment Section */}
      {attachment && (
        <div className="flex flex-col mt-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary to-[#7e7cff] flex items-center justify-center">
              <HiOutlinePaperClip className="w-5 h-5 text-white" />
            </div>
            <h5 className="text-xl font-bold text-secondary">Attachment</h5>
          </div>
          <a
            href={attachment}
            target="_blank"
            rel="noreferrer"
            download
            className="flex items-center gap-4 p-5 rounded-2xl bg-gray-50/80 hover:bg-white shadow-sm hover:shadow-md transition-all group max-w-md"
          >
            <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <AttachIcon className="w-6 h-6" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium text-secondary group-hover:text-primary transition-colors">
                {attachment.substring(attachment.lastIndexOf("/") + 1)}
              </span>
              <span className="text-sm text-gray-500">Click to download</span>
            </div>
          </a>
        </div>
      )}
    </div>
  );
};

export default DetailsTab;
